import { DOCUMENT } from '@angular/common';
import { Component, HostListener, Inject, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { SelectOption } from '@gainsystems/gains-ui-commons';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { PROJECT_NAME_MAX_LENGTH } from '../../../models/project-name.constants';

export type ProjectModalMode = 'create' | 'edit';

export interface ProjectModalData {
  mode?: ProjectModalMode;
  project?: {
    name?: string;
    description?: string;
    access?: string;
    color?: string;
  };
}

export interface ProjectModalResult {
  mode: ProjectModalMode;
  name: string;
  description: string;
  access: string;
  color: string;
}

const trimmedRequiredValidator = (control: AbstractControl): ValidationErrors | null => {
  const value = String(control.value ?? '').trim();
  return value ? null : { required: true };
};

@Component({
  standalone: false,
  selector: 'deo-create-new-project',
  templateUrl: './deo-create-new-project.component.html',
  styleUrls: ['./deo-create-new-project.component.scss']
})
export class DeoCreateNewProjectComponent implements OnDestroy {
  formGroup!: FormGroup;
  mode: ProjectModalMode;
  protected readonly nameMaxLength = PROJECT_NAME_MAX_LENGTH;
  private readonly _colorDropdownScopeClass = 'project-color-dropdown-scope';

  accessOptions: SelectOption[] = [
    { title: 'Only Me (Private)', value: 'Only Me (Private)' },
    { title: 'Everyone', value: 'Everyone' }
  ];

  colorOptions: SelectOption[] = [
    { title: 'Grey', value: '#9E9E9E' },
    { title: 'Red', value: '#E53935' },
    { title: 'Orange', value: '#FB8C00' },
    { title: 'Green', value: '#43A047' },
    { title: 'Blue', value: '#1E88E5' },
    { title: 'Violet', value: '#8E24AA' }
  ];

  nameControl = new FormControl('', [trimmedRequiredValidator, Validators.maxLength(PROJECT_NAME_MAX_LENGTH)]);
  descriptionControl = new FormControl('');
  accessControl = new FormControl<SelectOption | null>(this.accessOptions[0], [Validators.required]);
  colorControl = new FormControl<SelectOption | null>(this.colorOptions[0], [Validators.required]);
  constructor(
    private readonly _fb: FormBuilder,
    private readonly _modalRef: NzModalRef<DeoCreateNewProjectComponent, ProjectModalResult | undefined>,
    @Inject(DOCUMENT) private readonly _document: Document,
    @Inject(NZ_MODAL_DATA) private readonly _modalData: ProjectModalData
  ) {
    this.mode = this._modalData?.mode ?? 'create';

    this.formGroup = this._fb.group({
      projectName: this.nameControl,
      projectDescription: this.descriptionControl,
      projectAccess: this.accessControl,
      projectColor: this.colorControl
    });

    this.prefillFormForEdit();
  }

  onCancel(): void {
    this.disableColorDropdownScope();
    this._modalRef.destroy();
  }

  onSave(): void {
    if (!this.isFormSubmittable) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const name = (this.nameControl.value ?? '').trim();

    this._modalRef.close({
      mode: this.mode,
      name,
      description: (this.descriptionControl.value ?? '').trim(),
      access: this.accessControl.value?.value ?? this.accessOptions[0].value ?? 'Only Me (Private)',
      color: this.colorControl.value?.value ?? this.colorOptions[0].value ?? 'Grey'
    });
    this.disableColorDropdownScope();
    this.resetForm();
  }

  enableColorDropdownScope(): void {
    this._document.body.classList.add(this._colorDropdownScopeClass);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      this.disableColorDropdownScope();
      return;
    }

    const clickedColorSelect = !!target.closest('gains-select.color-select');
    const clickedDropdown = !!target.closest('.ant-select-dropdown');
    if (!clickedColorSelect && !clickedDropdown) {
      this.disableColorDropdownScope();
    }
  }

  get modalTitle(): string {
    return this.mode === 'edit' ? 'Edit Project' : 'Create New Project';
  }

  get primaryButtonLabel(): string {
    return this.mode === 'edit' ? 'Update' : 'Save';
  }

  get isFormSubmittable(): boolean {
    return this.formGroup.valid;
  }

  get isNameTooLong(): boolean {
    return this.nameControl.hasError('maxlength');
  }

  get selectedColorCode(): string {
    return (this.colorControl.value?.value as string) || '#9E9E9E';
  }

  private prefillFormForEdit(): void {
    if (this.mode !== 'edit' || !this._modalData?.project) {
      return;
    }

    const { name, description, access, color } = this._modalData.project;

    this.nameControl.setValue(name ?? '');
    this.descriptionControl.setValue(description ?? '');
    this.accessControl.setValue(this.getOptionByValue(this.accessOptions, access, this.accessOptions[0]));
    this.colorControl.setValue(this.getOptionByValue(this.colorOptions, color, this.colorOptions[0]));
  }

  private getOptionByValue(options: SelectOption[], value: string | undefined, fallback: SelectOption): SelectOption {
    if (!value) {
      return fallback;
    }
    return options.find(option => option.value === value) ?? fallback;
  }

  private resetForm(): void {
    this.nameControl.setValue('');
    this.descriptionControl.setValue('');
    this.accessControl.setValue(this.accessOptions[0]);
    this.colorControl.setValue(this.colorOptions[0]);
  }

  ngOnDestroy(): void {
    this.disableColorDropdownScope();
  }

  private disableColorDropdownScope(): void {
    this._document.body.classList.remove(this._colorDropdownScopeClass);
  }
}
